/**
 * @fileoverview Legacy saved-search migration.
 *
 * The explicit-trip-type feature made trip type explicit and tags the airport
 * endpoint with `type: 'airport'`. Saves written before that (or by an
 * in-between build) fail the strict validator in `localStorage.ts` — they lack
 * `tripType` and/or the airport tag. Rather than drop them, `startLegacyMigration`
 * recovers what it can and dispatches a `SearchFormData` patch to the store.
 *
 * ## How a raw blob is resolved
 * 1. Parse pickup/drop loosely (`isLocationSuggestion`); bail if neither is
 *    usable.
 * 2. Decide `tripType`: an explicit valid saved value wins; else infer it from
 *    which side is a tagged airport (`inferTripTypeFromAirportSide`); else
 *    default to `AIRPORT_DROP_OFF`. This decides which field is the airport.
 * 3. If the airport side already carries `type: 'airport'` AND is submittable
 *    (has a placeId or coords), trust it and hydrate as-is — no network call.
 * 4. Otherwise (legacy: airport field holds a plain location, no tag, or a
 *    tagged airport missing coords), look up the nearest airport to the
 *    *location* side's coords via autocomplete and use that as the airport field.
 *
 * Only submittable values (placeId or lat/lng) are ever written to a field — a
 * value the review route can't serialize is dropped, leaving that field empty
 * for the user rather than producing a broken downstream fetch.
 *
 * ## Cases / outcomes
 * - **Valid new-format save** → never reaches here (loads directly).
 * - **Explicit tripType, airport tagged + submittable** → hydrated as-is (step 3).
 * - **No tripType, airport tagged** → tripType inferred from the tag, then
 *   hydrated as-is (steps 2–3); no redundant lookup.
 * - **Airport field holds a plain location** → nearest-airport lookup fills the
 *   airport field, the location side is preserved if submittable (step 4).
 * - **Location side is itself an airport** (e.g. legacy save with the airport as
 *   the "from" location) → the lookup returns that same airport. We put it in the
 *   airport field and recover the *other* raw value into the location field if
 *   it's a usable, different, submittable place; otherwise the location field is
 *   left empty for the user (`isSamePlace` guards both checks).
 * - **Nothing usable / API fails / no airport found / unsubmittable** → return
 *   the best-effort `base` (or `null`); the user fills the rest manually.
 *
 * All API work is abortable via the caller's signal (wired to unmount).
 */
import {
	getLocationsSuggestions,
	getPlaceDetails,
	inferTripTypeFromAirportSide,
	isTripType,
	type LocationSuggestion,
	TRIP_TYPE,
	TRIP_TYPE_CONFIG,
	type TripType,
} from '@ixigo/cab-core/location';
import { loadRawSearchFormData } from './localStorage';
import type { SearchFormActions, SearchFormData } from './store/types';

/**
 * Resolve one raw saved blob into a `SearchFormData` patch (see file overview
 * for the per-case behavior). Returns the patch ready for `updateFormData`, or
 * `null` when the blob has nothing usable.
 */
async function migrateLegacySave(
	raw: unknown,
	opts: { signal: AbortSignal },
): Promise<Partial<SearchFormData> | null> {
	if (!raw || typeof raw !== 'object') return null;
	const {
		pickup: rawPickup,
		drop: rawDrop,
		tripType: rawTripType,
		dateTime: rawDateTime,
	} = raw as Record<string, unknown>;

	const pickup = isLocationSuggestion(rawPickup) ? rawPickup : undefined;
	const drop = isLocationSuggestion(rawDrop) ? rawDrop : undefined;
	if (!pickup && !drop) return null;

	// Trust an explicit saved tripType; otherwise infer it from which side is a
	// tagged airport (so a save with the airport on pickup isn't mis-assigned to
	// the AIRPORT_DROP_OFF default). Falls back to drop-off when inconclusive.
	const tripType: TripType =
		(isTripType(rawTripType) ? rawTripType : undefined) ??
		inferTripTypeFromAirportSide(pickup?.type, drop?.type) ??
		TRIP_TYPE.AIRPORT_DROP_OFF;
	const dateTime = typeof rawDateTime === 'string' ? rawDateTime : undefined;

	const { airportField } = TRIP_TYPE_CONFIG[tripType];
	const locationField = airportField === 'pickup' ? 'drop' : 'pickup';
	const locationValue = locationField === 'pickup' ? pickup : drop;
	const airportValue = airportField === 'pickup' ? pickup : drop;

	// Only write submittable values to a field. Trust the airport side from the
	// blob only if it's tagged AND submittable; otherwise fall through to the
	// lookup. The location side is likewise dropped if it can't be serialized.
	const airportSide =
		airportValue?.type === 'airport' && isSubmittable(airportValue)
			? airportValue
			: undefined;
	const locationSide =
		locationValue && isSubmittable(locationValue) ? locationValue : undefined;
	const base: Partial<SearchFormData> = {
		[airportField]: airportSide,
		[locationField]: locationSide,
		tripType,
		...(dateTime ? { dateTime } : {}),
	};

	// Airport side already resolved from the blob, or no location-side value to
	// query against → hydrate as-is, no nearest-airport lookup needed.
	if (airportSide || !locationValue) return base;

	const coords = await resolveCoords(locationValue, opts.signal);
	if (!coords) return base;

	let result: Awaited<ReturnType<typeof getLocationsSuggestions>>;
	try {
		result = await getLocationsSuggestions(
			{
				query: '',
				tripType,
				inputFieldType: airportField === 'pickup' ? 'PICKUP' : 'DROP',
				userCoords: { latitude: coords.lat, longitude: coords.lng },
			},
			{ signal: opts.signal },
		);
	} catch {
		return base;
	}

	// Need a submittable airport (placeId or coords) — otherwise the form value
	// can't be serialized downstream, so abort to base and let the user pick.
	const firstAirport = result.items.find(
		(item) => item.type === 'airport' && isSubmittable(item),
	);
	if (!firstAirport) return base;

	// The location side may itself be an airport (e.g. a legacy save with the
	// airport as the "from" location). The nearest-airport lookup then returns
	// that same airport, so move it to the airport field and recover the value we
	// discarded from the airport slot into the location field — only if it's a
	// usable, different, submittable place; otherwise leave that field empty.
	const samePlace = isSamePlace(firstAirport, locationValue);
	const recoveredLocation =
		samePlace &&
		airportValue &&
		isSubmittable(airportValue) &&
		!isSamePlace(airportValue, firstAirport)
			? airportValue
			: undefined;
	return {
		...base,
		[airportField]: firstAirport,
		// On a collision, replace/clear the location side; otherwise keep base's.
		...(samePlace ? { [locationField]: recoveredLocation } : {}),
	};
}

/** A value is usable only if the review route can serialize it: placeId or coords. */
function isSubmittable(value: LocationSuggestion): boolean {
	return (
		!!value.placeId ||
		(typeof value.lat === 'number' && typeof value.lng === 'number')
	);
}

/**
 * Whether two suggestions resolve to the same place: an exact placeId match when
 * both have one (Google places), else a label match (DB airports are coord-
 * identified and carry no placeId, so fall back to mainText). Two values with no
 * comparable identity (no shared placeId, missing mainText) are NOT same.
 */
function isSamePlace(a: LocationSuggestion, b: LocationSuggestion): boolean {
	if (a.placeId && b.placeId) return a.placeId === b.placeId;
	return !!a.mainText && a.mainText === b.mainText;
}

/**
 * Loose check that a value looks like a LocationSuggestion. Requires plain
 * object (not array, not Date) and at least one identifying field — so we
 * don't carry forward `[]` / `{}` / `new Date()` from a corrupted blob.
 */
function isLocationSuggestion(value: unknown): value is LocationSuggestion {
	if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
	const obj = value as Record<string, unknown>;
	return (
		typeof obj.mainText === 'string' ||
		typeof obj.formattedAddress === 'string' ||
		typeof obj.placeId === 'string' ||
		(typeof obj.lat === 'number' && typeof obj.lng === 'number')
	);
}

async function resolveCoords(
	value: LocationSuggestion,
	signal: AbortSignal,
): Promise<{ lat: number; lng: number } | null> {
	if (typeof value.lat === 'number' && typeof value.lng === 'number') {
		return { lat: value.lat, lng: value.lng };
	}
	if (!value.placeId) return null;
	if (signal.aborted) return null;
	try {
		const details = await getPlaceDetails(value.placeId);
		if (signal.aborted) return null;
		if (
			details &&
			typeof details.lat === 'number' &&
			typeof details.lng === 'number'
		) {
			return { lat: details.lat, lng: details.lng };
		}
	} catch {
		// fall through
	}
	return null;
}

/**
 * Read the raw saved-search blob and, if it looks legacy, run the BE-driven
 * migration and dispatch the result via `searchFormActions.updateFormData`.
 * Returns an `abort` cleanup the caller (a `useEffect`) wires to unmount so
 * we don't dispatch into a torn-down store.
 *
 * `adjustDateTime` lets the caller massage the dateTime (e.g. bump stale
 * saves forward by 3 hours) without this helper knowing the rules.
 */
export function startLegacyMigration(
	searchFormActions: SearchFormActions,
	adjustDateTime: (dateTime?: string) => string,
): (() => void) | undefined {
	const raw = loadRawSearchFormData();
	if (!raw) return;
	const controller = new AbortController();
	migrateLegacySave(raw, { signal: controller.signal })
		.then((partial) => {
			if (!partial || controller.signal.aborted) return;
			searchFormActions.updateFormData({
				...partial,
				dateTime: adjustDateTime(partial.dateTime),
			});
		})
		.catch(() => {
			// Best-effort — user starts fresh on failure.
		});
	return () => controller.abort();
}
